import { apiGet, apiPost } from './grampsApi';
import type { components, paths } from './gramps-openapi';
import type { GrampsEventMedia, GrampsNote } from '../utils';
import { republicanDateString } from '../republicanCalendar';

export type RestPerson = components['schemas']['Person'];
export type RestEvent = components['schemas']['Event'];
export type RestFamily = components['schemas']['Family'];
export type RestNote = components['schemas']['Note'];
export type RestPlace = components['schemas']['Place'];
export type RestMedia = components['schemas']['Media'];
export type RestCitation = components['schemas']['Citation'];
export type RestSource = components['schemas']['Source'];
export type RestDate = components['schemas']['Date'];
export type RestTransaction = components['schemas']['UndoTransaction'];
export type RestTag = components['schemas']['Tag'];

type PeopleQuery = NonNullable<NonNullable<paths['/api/people/']['get']>['parameters']['query']>;
type FamiliesQuery = NonNullable<NonNullable<paths['/api/families/']['get']>['parameters']['query']>;
type EventsQuery = NonNullable<NonNullable<paths['/api/events/']['get']>['parameters']['query']>;
type CitationsQuery = NonNullable<NonNullable<paths['/api/citations/']['get']>['parameters']['query']>;
type SourcesQuery = NonNullable<NonNullable<paths['/api/sources/']['get']>['parameters']['query']>;
type TransactionsHistoryQuery = NonNullable<NonNullable<paths['/api/transactions/history/']['get']>['parameters']['query']>;

interface PersonExtended {
    notes?: RestNote[];
    events?: RestEvent[];
    media?: RestMedia[];
    families?: RestFamily[];
    parent_families?: RestFamily[];
}

interface FamilyExtended {
    notes?: RestNote[];
    events?: RestEvent[];
}

interface EventExtended {
    notes?: RestNote[];
    place?: RestPlace;
    media?: RestMedia[];
    citations?: RestCitation[];
}

interface CitationExtended {
    notes?: RestNote[];
    source?: RestSource;
}

export function personExtended(person: RestPerson): PersonExtended {
    return (person.extended ?? {}) as PersonExtended;
}

export function familyExtended(family: RestFamily): FamilyExtended {
    return (family.extended ?? {}) as FamilyExtended;
}

export function eventExtended(event: RestEvent): EventExtended {
    return (event.extended ?? {}) as EventExtended;
}

export function citationExtended(citation: RestCitation): CitationExtended {
    return (citation.extended ?? {}) as CitationExtended;
}

function isAncestorOfRule(personGrampsId: string): string {
    return JSON.stringify({ rules: [{ name: 'IsAncestorOf', values: [personGrampsId, '1'] }] });
}

function familyHasAncestorRule(personGrampsId: string): string {
    return JSON.stringify({
        rules: [
            {
                namespace: 'Person',
                rules: [{ name: 'IsAncestorOf', values: [personGrampsId, '1'] }],
            },
        ],
    });
}

export async function fetchAncestorPersons(personGrampsId: string, token: string | undefined): Promise<RestPerson[]> {
    const result = await apiGet('/api/people/', {
        token,
        query: {
            rules: isAncestorOfRule(personGrampsId),
            extend: 'note_list,event_ref_list,media_list' as PeopleQuery['extend'],
        } as PeopleQuery,
    });
    return (result ?? []) as unknown as RestPerson[];
}

/**
 * Extends exactly what the drawer and the tabs read, rather than `all`.
 *
 * Including the two family lists means the person's families arrive with the person,
 * removing a round trip from the drawer's chain — the families' own `event_ref_list`
 * and `child_ref_list` are plain handle arrays, so nothing else is needed from them.
 */
export async function fetchPersonByGrampsId(personGrampsId: string, token: string | undefined): Promise<RestPerson | undefined> {
    const result = await apiGet('/api/people/', {
        token,
        query: {
            gramps_id: personGrampsId,
            extend: 'note_list,event_ref_list,media_list,family_list,parent_family_list' as PeopleQuery['extend'],
        } as PeopleQuery,
    });
    return ((result ?? []) as unknown as RestPerson[])[0];
}

/**
 * Every person in the tree, restricted to the fields the person picker needs.
 * Without `keys` this returns the full record for each person, which is a large
 * payload on big trees.
 */
export async function fetchAllPeople(token: string | undefined): Promise<RestPerson[]> {
    const result = await apiGet('/api/people/', {
        token,
        query: {
            keys: 'handle,gramps_id,primary_name' as unknown as PeopleQuery['keys'],
        } as PeopleQuery,
    });
    return (result ?? []) as unknown as RestPerson[];
}

const DIACRITIC_CLASSES: Record<string, string> = {
    a: '[aàâäáãå]',
    c: '[cç]',
    e: '[eéèêë]',
    i: '[iîïíì]',
    n: '[nñ]',
    o: '[oôöóòõ]',
    u: '[uùûüú]',
    y: '[yÿý]',
};

function searchWordRegex(word: string): string {
    const folded = word.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    let pattern = '(?i)';
    for (const ch of folded) {
        if (ch in DIACRITIC_CLASSES) pattern += DIACRITIC_CLASSES[ch];
        else if (ch === 'œ') pattern += '(?:œ|oe)';
        else if (ch === 'æ') pattern += '(?:æ|ae)';
        else if (ch === '-' || ch === ' ') pattern += '[- ]';
        else if (ch === "'" || ch === '’') pattern += "['’]";
        else pattern += ch.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    }
    return pattern;
}

/**
 * Live-search candidates for the main person picker via Gramps' structured query
 * endpoint (`/api/people/query/`), reading SQLite columns directly rather than
 * `/api/search/` (whose FTS5 index is unbuilt on newer trees and lags behind edits).
 */
export async function searchPeopleByName(term: string, token: string | undefined, limit = 25): Promise<RestPerson[]> {
    const words = term.trim().split(/[\s,*]+/).filter(Boolean);
    if (words.length === 0) return [];
    const columns = ['given_name', 'surname', 'gramps_id'];
    const result = await apiPost('/api/people/query/', {
        token,
        body: {
            select: ['handle', 'gramps_id', 'primary_name'],
            where: words.map((word) => ({
                or: columns.map((column) => ({
                    column,
                    op: 'regex' as const,
                    value: searchWordRegex(word),
                })),
            })),
            order_by: [
                { column: 'surname', direction: 'asc' },
                { column: 'given_name', direction: 'asc' },
            ],
            limit,
            locale: 'fr',
            count: false,
        },
    });
    return ((result?.items ?? []) as unknown as RestPerson[]);
}

export async function fetchFamiliesByHandles(handles: string[], token: string | undefined): Promise<RestFamily[]> {
    if (handles.length === 0) {
        return [];
    }
    const result = await apiGet('/api/families/', {
        token,
        query: {
            handles: handles.join(',') as unknown as FamiliesQuery['handles'],
            extend: 'note_list,event_ref_list' as FamiliesQuery['extend'],
        } as FamiliesQuery,
    });
    return (result ?? []) as unknown as RestFamily[];
}

export async function fetchAncestorFamilies(personGrampsId: string, token: string | undefined): Promise<RestFamily[]> {
    const result = await apiGet('/api/families/', {
        token,
        query: {
            rules: familyHasAncestorRule(personGrampsId),
            extend: 'note_list,event_ref_list' as FamiliesQuery['extend'],
        } as FamiliesQuery,
    });
    return (result ?? []) as unknown as RestFamily[];
}

export async function fetchPeopleByHandles(handles: string[], token: string | undefined, extend = 'event_ref_list'): Promise<RestPerson[]> {
    if (handles.length === 0) {
        return [];
    }
    const result = await apiGet('/api/people/', {
        token,
        query: {
            handles: handles.join(',') as unknown as PeopleQuery['handles'],
            extend: extend as PeopleQuery['extend'],
        } as PeopleQuery,
    });
    return (result ?? []) as unknown as RestPerson[];
}

// `citation_list` is deliberately absent: an Event already carries the citation
// handles as a plain array, and RestEventItem resolves the records on demand.
const EVENT_EXTEND_KEYS = 'note_list,place,media_list';

export interface FetchEventsOptions {
    /** Add each event's participants (people with their role, witnesses included)
     * to the same response, as `profile.participants`. It roughly doubles the
     * payload, so only the person drawer, which shows them, asks for it. */
    participants?: boolean;
}

async function fetchEventBatch(handles: string[], token: string | undefined, options: FetchEventsOptions): Promise<RestEvent[]> {
    const result = await apiGet('/api/events/', {
        token,
        query: {
            handles: handles.join(',') as unknown as EventsQuery['handles'],
            extend: EVENT_EXTEND_KEYS as EventsQuery['extend'],
            ...(options.participants ? { profile: 'participants' as EventsQuery['profile'] } : {}),
        } as EventsQuery,
    });
    return (result ?? []) as unknown as RestEvent[];
}

export interface EventParticipant {
    handle: string;
    grampsId?: string;
    name: string;
    /** 'M' or 'F' when known. */
    sex?: string;
}

/** Roles of the people who attended an event without it being about them. */
export const ATTENDANCE_ROLES = ['Witness', 'Informant', 'Godparent'] as const;
export type AttendanceRole = typeof ATTENDANCE_ROLES[number];

export function isAttendanceRole(role: string | undefined): role is AttendanceRole {
    return (ATTENDANCE_ROLES as readonly string[]).includes(role ?? '');
}

interface ProfileParticipantPerson {
    handle?: string;
    gramps_id?: string;
    name_given?: string;
    name_surname?: string;
    name_display?: string;
    sex?: string;
}

interface ProfileParticipants {
    people?: { role?: string; person?: ProfileParticipantPerson }[];
    families?: { role?: string; family?: { father?: ProfileParticipantPerson; mother?: ProfileParticipantPerson } }[];
}

function participants(event: RestEvent): ProfileParticipants {
    return (event.profile?.participants as ProfileParticipants | undefined) ?? {};
}

function toParticipant(person: ProfileParticipantPerson | undefined): EventParticipant[] {
    if (!person?.handle) return [];
    const name = `${person.name_given ?? ''} ${person.name_surname ?? ''}`.trim() || person.name_display || '?';
    return [{ handle: person.handle, grampsId: person.gramps_id, name, sex: person.sex }];
}

/** People linked to the event with this role (witnesses, informants,
 * godparents), from `profile.participants`: only present when the events were
 * fetched with `participants: true`. */
export function restEventParticipantsWithRole(event: RestEvent, wanted: AttendanceRole): EventParticipant[] {
    return (participants(event).people ?? []).flatMap(({ role, person }) => role === wanted ? toParticipant(person) : []);
}

/** Whom the event is about: its primary people, or the couple of a family event. */
export function restEventPrincipals(event: RestEvent): EventParticipant[] {
    const { people = [], families = [] } = participants(event);
    return [
        ...people.flatMap(({ role, person }) => role === 'Primary' ? toParticipant(person) : []),
        ...families.flatMap(({ family }) => [...toParticipant(family?.father), ...toParticipant(family?.mother)]),
    ];
}

/**
 * Short stable digest of a handle list, for use as a React Query key component.
 * Passing the raw array makes React Query re-hash thousands of strings on every
 * render; the digest is computed once per change instead.
 */
export function handlesDigest(handles: string[]): string {
    let hash = 5381;
    for (const handle of handles) {
        for (let index = 0; index < handle.length; index += 1) {
            hash = ((hash * 33) ^ handle.charCodeAt(index)) >>> 0;
        }
    }
    return `${handles.length}-${hash.toString(36)}`;
}

export async function fetchEventsByHandles(handles: string[], token: string | undefined, options: FetchEventsOptions = {}): Promise<RestEvent[]> {
    const batchSize = 100;
    const uniqueHandles = [...new Set(handles)];
    if (uniqueHandles.length === 0) {
        return [];
    }
    const batches: string[][] = [];

    for (let index = 0; index < uniqueHandles.length; index += batchSize) {
        batches.push(uniqueHandles.slice(index, index + batchSize));
    }

    const results = await Promise.all(batches.map((batch) => fetchEventBatch(batch, token, options)));
    return results.flat();
}

export type LatestTransaction = { id: number; timestamp: number };

/**
 * Id (and server timestamp) of the most recently committed transaction, scoped to
 * `afterTimestamp` when given. Passing the last-seen timestamp back in lets the backend
 * filter the history query itself instead of counting/scanning transactions we've already
 * seen on every poll — the response payload was already just 1 row (pagesize 1, sorted
 * descending), but without `after` the backend still has to weigh the whole history to
 * compute that.
 */
export async function fetchLatestTransactionId(
    token: string | undefined,
    afterTimestamp?: number,
): Promise<LatestTransaction | null> {
    const result = await apiGet('/api/transactions/history/', {
        token,
        query: {
            sort: '-id',
            pagesize: 1,
            ...(afterTimestamp !== undefined ? { after: afterTimestamp } : {}),
        } as TransactionsHistoryQuery,
    });
    const transactions = (result ?? []) as unknown as RestTransaction[];
    const latest = transactions[0];
    if (latest?.id === undefined || latest.timestamp === undefined) return null;
    return { id: latest.id, timestamp: latest.timestamp };
}

/**
 * Relationship label between two people ("son arrière-grand-père", ...).
 *
 * Measured on the 2-CPU production box: depth=20 costs ~970 ms, depth=5 ~510 ms —
 * the relationship calculator's cost scales with the depth it is allowed to search.
 * 10 is well beyond the deepest line in the tree while halving the work. Callers
 * should treat this as a slow, non-blocking lookup.
 */
export async function fetchRelationship(
    mainHandle: string,
    selectedHandle: string,
    token: string | undefined,
    depth = 10,
): Promise<string | null> {
    const result = await apiGet('/api/relations/{handle1}/{handle2}', {
        token,
        pathParams: { handle1: mainHandle.replace(/^_+/, ''), handle2: selectedHandle.replace(/^_+/, '') },
        query: { locale: 'fr', depth },
    }).catch(() => null);

    return result && typeof result === 'object' && 'relationship_string' in result
        ? String(result.relationship_string ?? '').trim() || null
        : null;
}

export async function fetchCitationsByHandles(handles: string[], token: string | undefined): Promise<RestCitation[]> {
    if (handles.length === 0) {
        return [];
    }
    const result = await apiGet('/api/citations/', {
        token,
        query: {
            handles: handles.join(',') as unknown as CitationsQuery['handles'],
            extend: 'note_list,source_handle' as CitationsQuery['extend'],
        } as CitationsQuery,
    });
    return (result ?? []) as unknown as RestCitation[];
}

export async function fetchSourcesByHandles(handles: string[], token: string | undefined): Promise<RestSource[]> {
    if (handles.length === 0) {
        return [];
    }
    const result = await apiGet('/api/sources/', {
        token,
        query: {
            handles: handles.join(',') as unknown as SourcesQuery['handles'],
        } as SourcesQuery,
    });
    return (result ?? []) as unknown as RestSource[];
}

function pad2(n: number): string {
    return n < 10 ? `0${n}` : `${n}`;
}

function formatDateTriple(day: number, month: number, year: number): string {
    if (!year) return '';
    if (!day && !month) return String(year);
    if (!day) return `${pad2(month)}/${year}`;
    return `${pad2(day)}/${pad2(month)}/${year}`;
}

function dateModifierWord(modifier: number | undefined): string {
    switch (modifier) {
        case 1: return 'avant ';
        case 2: return 'après ';
        case 3: return 'environ ';
        default: return '';
    }
}

// Modifier codes from gramps.gen.lib.date.Date: 0=exact 1=before 2=after 3=about 4=range 5=span 6=textonly 7=from 8=to
export function restEventDateString(date: RestDate | undefined): string {
    if (!date) {
        return '';
    }

    const modifier = date.modifier ?? 0;
    const vals = (date.dateval ?? []) as Array<number | boolean>;
    const asNum = (v: number | boolean | undefined): number => (typeof v === 'number' ? v : 0);

    if ((modifier === 4 || modifier === 5) && vals.length >= 8) {
        const start = formatDateTriple(asNum(vals[0]), asNum(vals[1]), asNum(vals[2]));
        const stop = formatDateTriple(asNum(vals[4]), asNum(vals[5]), asNum(vals[6]));
        if (start || stop) {
            return `entre ${start || '?'} et ${stop || '?'}`;
        }
    }

    if (modifier === 6 && date.text) {
        return date.text;
    }

    if (vals.length >= 3) {
        const label = formatDateTriple(asNum(vals[0]), asNum(vals[1]), asNum(vals[2]));
        if (label) {
            return `${dateModifierWord(modifier)}${label}`;
        }
    }

    if (date.text) {
        return date.text;
    }

    if (date.year) {
        return `${dateModifierWord(modifier)}${date.year}`;
    }

    return '';
}

/** The date in the Republican calendar when it falls in 1793-1805, for a tooltip;
 * a range gets both ends. Undefined for dates outside it or without a full day. */
export function restEventRepublicanDateString(date: RestDate | undefined): string | undefined {
    if (!date || date.modifier === 6) {
        return undefined;
    }
    const vals = (date.dateval ?? []) as Array<number | boolean>;
    const asNum = (v: number | boolean | undefined): number => (typeof v === 'number' ? v : 0);
    const start = republicanDateString(asNum(vals[2]), asNum(vals[1]), asNum(vals[0]));
    if ((date.modifier === 4 || date.modifier === 5) && vals.length >= 8) {
        const stop = republicanDateString(asNum(vals[6]), asNum(vals[5]), asNum(vals[4]));
        return start || stop ? `entre ${start ?? '?'} et ${stop ?? '?'}` : undefined;
    }
    return start && `${dateModifierWord(date.modifier)}${start}`;
}

function extractRestMediaFileName(path: string | undefined): string | undefined {
    if (!path) {
        return undefined;
    }
    const clean = path.split('?')[0].split('#')[0];
    return clean.split('/').pop() || clean;
}

export function mapRestMedia(medias: RestMedia[] | undefined): GrampsEventMedia[] {
    return (medias ?? [])
        .map((media): GrampsEventMedia | undefined => {
            const file = extractRestMediaFileName(media.path);
            if (!file) {
                return undefined;
            }
            return { file, handle: media.handle, grampsId: media.gramps_id };
        })
        .filter((item): item is GrampsEventMedia => Boolean(item));
}

export function restPersonName(person: RestPerson): string {
    const name = person.primary_name;
    const first = name?.first_name ?? '';
    const nickname = name?.nick;
    const surname = name?.surname_list?.[0]?.surname ?? '';
    return (nickname ? `${first} "${nickname}" ${surname}` : `${first} ${surname}`).trim();
}

/** First name (and nickname, if any) without the surname -- for contexts like a
 * child listed under their parent, where the surname is implicit. */
export function restPersonGivenName(person: RestPerson): string {
    const name = person.primary_name;
    const first = name?.first_name ?? '';
    const nickname = name?.nick;
    return (nickname ? `${first} "${nickname}"` : first).trim();
}

export function restPlaceName(place: RestPlace | undefined): string | undefined {
    return place?.name?.value;
}

export function toGrampsNote(note: RestNote): GrampsNote {
    return {
        "@handle": note.handle ?? '',
        "@id": note.gramps_id ?? '',
        "@change": String(note.change ?? ''),
        text: note.text?.string ?? '',
    };
}

function restDateComponents(date: RestDate | undefined): [number, number, number] {
    const vals = (date?.dateval ?? []) as Array<number | boolean>;
    const asNum = (v: number | boolean | undefined): number => (typeof v === 'number' ? v : 0);
    return [asNum(vals[0]), asNum(vals[1]), asNum(vals[2])];
}

export function restDateToJsDate(date: RestDate | undefined): Date | undefined {
    if (!date) {
        return undefined;
    }
    const [day, month, year] = restDateComponents(date);
    if (!year) {
        return undefined;
    }
    return new Date(year, Math.max(0, month - 1), day || 1);
}

export function restPersonIsMale(person: RestPerson | undefined): boolean {
    return person?.gender === 1;
}
